import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import UploadIcon from '@material-ui/icons/CloudUpload';
import SearchBar from 'tuc-webui-base/components/SearchBar';
import { extendViewService } from 'tuc-webui-base/services/ViewService';
import { useRowNumber, usePendingSelection } from 'tuc-webui-base/components/TableViewComponent';
import MessageDialog from 'grb-webui/components/MessageDialog';
import FilterBar, { withPageFilter } from 'grb-webui/components/FilterBar';
import { TABLE_PANEL_TYPE } from 'grb-webui/ComponentTypes';
import { withStoreData } from 'grb-webui/store';
import AppMainPage, { FIT_TO_PAGE_VARIANT } from '../../components/AppMainPage';
import {
  STORAGE_OBJECTS_ENTITY,
} from '../../Entities';
import StoredObjectButtons from './StoredObjectButtons';
import ObjectFilter from './ObjectFilter';
import './RepositoryContainer.scss';
import UploadDialog from './UploadDialog';

export default (options = {}) => {
  const ViewServiceProperties = {
    componentsByName: {
      StoredObjectButtons,
    },
    editors: [],
  };

  const makeDialog = (opt = {}, defaultDialog) => {
    const { dialog } = opt;
    if (dialog) {
      if (dialog.create) {
        const { create, ...params } = dialog;
        return [dialog.create(options), params];
      }
      if (typeof dialog === 'function') {
        return [dialog(options)];
      }
    }
    return defaultDialog ? [defaultDialog(options), dialog] : [];
  };
  const [UploadObjectsDialog, uploadDialogProps = {}] = makeDialog(options.upload, UploadDialog);

  const RepositoryContainer = (props) => {
    const {
      filter,
      arrayFilter,
      onFilterChange,
      filterEditor,
      onUpdateObjects,
      storageObjects,
      search,
      onSearchChange,
      configuration,
      ...rest
    } = props;
    const {
      viewService,
      repositoryService,
      onSnackbarMessage,
    } = props;
    const [dialogMessage, setDialogMessage] = useState({ open: false });
    const [uploadDialog, setUploadDialog] = useState({ open: false });
    const [TablePanel] = useState(() => viewService.getComponent(TABLE_PANEL_TYPE));
    const storageObjectsCount = useRowNumber(storageObjects, false, options.serverFiltering);
    const [extViewService] = useState(() => extendViewService(viewService, ViewServiceProperties));
    const [tableModel] = useState(() => {
      const handleDeleteObjects = (objects) => {
        repositoryService.deleteObjects(objects)
        .then(() => {
          onSnackbarMessage({ message: 'Objects deleted' });
        })
        .catch(e => viewService.onError(e));
      };

      return {
        canDelete: true,
        canCheck: true,
        onDeleteObject: handleDeleteObjects,
      };
    });
    const { selected, setPendingSelectionId, onSelect } = usePendingSelection(null, storageObjects);

    // Message dialog
    const handleCloseMessage = useCallback(() => {
      setDialogMessage({ open: false });
    }, []);

    // Upload dialog
    const handleUploadDialog = useCallback(() => {
      const handleClose = (objects) => {
        setUploadDialog({ open: false });
        if (objects && objects.length) {
          setPendingSelectionId(objects[0].id);
          if (onUpdateObjects()) {
            onUpdateObjects();
          }
          onSnackbarMessage({ message: `${objects.length} objects uploaded` });
        }
      };

      const msg = repositoryService.validateUpload();
      if (msg) {
        setDialogMessage({ title: 'Upload error', message: msg, open: true, onClose: handleCloseMessage });
      } else {
        setUploadDialog({ open: true, onClose: handleClose });
      }
    }, [onSnackbarMessage, repositoryService]);

    const filteredObjects = arrayFilter ? arrayFilter(storageObjects) : storageObjects;
    //
    // Header
    //
    const HeaderBar = (
      <React.Fragment>
        <FilterBar
          {...rest}
          filter={filter || {}}
          filterEditor={filterEditor}
          onFilterChange={onFilterChange}
        />
        <SearchBar
          {...props}
          placeholder="Search objects table..."
          value={search}
          onChange={onSearchChange}
        />
        <div className="grb-main-page-header-buttons">
          <Button
            variant="contained"
            color="primary"
            aria-label="add"
            onClick={handleUploadDialog}
            id="upload-objects"
          >
            <UploadIcon />Upload
          </Button>
        </div>
      </React.Fragment>
    );

    return (
      <AppMainPage
        route={props.route}
        variant={FIT_TO_PAGE_VARIANT}
        TrailingBar={HeaderBar}
        className="grb-repository-page"
        rowCount={storageObjectsCount}
        help={configuration && configuration.help}
      >
        <div className={classnames('grb-repository-table-panel', 'grb-fullpage', 'MuiPaper-elevation4')}>
          <TablePanel
            {...rest}
            {...rest.component}
            storageObjects={filteredObjects}
            viewService={extViewService}
            storageObjectsCount={storageObjectsCount}
            tableModel={tableModel} /* For deleting several objects from the table */
            selected={selected}
            onSelect={onSelect}
          />
          {dialogMessage.open && (<MessageDialog {...dialogMessage} />)}
          {uploadDialog.open && (<UploadObjectsDialog {...rest} {...uploadDialogProps} {...uploadDialog} />)}
        </div>
      </AppMainPage>
    );
  };

  RepositoryContainer.propTypes = {
    storageObjects: PropTypes.arrayOf(PropTypes.shape({})),
    viewService: PropTypes.shape({
      onError: PropTypes.func,
      getComponent: PropTypes.func,
    }).isRequired,
    route: PropTypes.shape({}).isRequired,
    repositoryService: PropTypes.shape({
      deleteObjects: PropTypes.func,
      validateUpload: PropTypes.func,
    }).isRequired,
    component: PropTypes.shape({}).isRequired,
    arrayFilter: PropTypes.func,
    onFilterChange: PropTypes.func,
    filter: PropTypes.shape({}),
    filterEditor: PropTypes.shape({}),
    onSnackbarMessage: PropTypes.func.isRequired,
    onUpdateObjects: PropTypes.func,
    search: PropTypes.string,
    onSearchChange: PropTypes.func,
    configuration: PropTypes.shape({
      help: PropTypes.shape({}),
    }),
  };

  RepositoryContainer.defaultProps = {
    storageObjects: undefined,
    filter: undefined,
    arrayFilter: undefined,
    onFilterChange: undefined,
    filterEditor: undefined,
    onUpdateObjects: undefined,
    search: undefined,
    onSearchChange: undefined,
    configuration: undefined,
  };

  return withPageFilter(
    withStoreData(RepositoryContainer, {
      entities: [{ name: STORAGE_OBJECTS_ENTITY, getQuery: (pps) => pps.serverQuery }],
      onUpdateStore: 'onUpdateObjects',
    }),
    ObjectFilter(options),
  );
};
